import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Search, FolderKanban, Wallet, Activity, CalendarClock, GitBranch, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API, { apiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ErpHeading, ErpStatus, ErpPagination, useErpList } from '../../components/erp/ErpShared';
import { PRJ_STATUS, money } from '../../components/erp/projectConfig';
import ProjectForm from '../../components/erp/ProjectForm';
import ProjectDetail from '../../components/erp/ProjectDetail';
import '../../components/erp/assets.css';

function ConvertDialog({ onClose, onConverted }) {
  const [tenders, setTenders] = useState(null);
  useEffect(() => { API.get('/erp/projects/won-tenders').then(r => setTenders(r.data)).catch(() => setTenders([])); }, []);
  const convert = async t => {
    const reason = window.prompt(`Convertești licitația „${t.title}” în proiect?`); if (!reason) return;
    try { await API.post('/erp/projects/convert-tender', { tender_id: t.id, reason }); toast.success('Proiect creat din licitație'); onConverted(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return <Dialog open onOpenChange={o => !o && onClose()}><DialogContent className="erp-modal" data-testid="convert-dialog">
    <DialogHeader><DialogTitle>Creează proiect din licitație câștigată</DialogTitle><DialogDescription>Se păstrează legătura cu licitația sursă; conversia nu se dublează.</DialogDescription></DialogHeader>
    {tenders === null ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
      : !tenders.length ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-tenders">Nu există licitații disponibile pentru conversie. Creează un proiect manual.</p>
      : <div className="space-y-2 max-h-[50vh] overflow-y-auto">{tenders.map(t => <div key={t.id} className="flex items-center justify-between gap-3 border border-white/5 rounded-lg px-3 py-2">
          <div className="min-w-0"><p className="text-sm truncate">{t.title}</p><p className="text-xs text-muted-foreground">{t.authority || '—'}{t.county ? ` · ${t.county}` : ''}{t.value ? ` · ${money(t.value)}` : ''}</p></div>
          <Button size="sm" variant={t.converted ? 'ghost' : 'outline'} disabled={t.converted} onClick={() => convert(t)}>{t.converted ? 'Convertit' : 'Convertește'}</Button></div>)}</div>}
  </DialogContent></Dialog>;
}

export default function ProjectsPage() {
  const { permissions } = useOutletContext();
  const canWrite = permissions.includes('projects.write');
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [statsV, setStatsV] = useState(0);
  const [form, setForm] = useState(null);
  const [detailId, setDetailId] = useState('');
  const [converting, setConverting] = useState(false);

  const query = useMemo(() => `${q ? `&q=${encodeURIComponent(q)}` : ''}${status ? `&status=${status}` : ''}`, [q, status]);
  const list = useErpList('/erp/projects', query);
  const loadStats = useCallback(() => API.get('/erp/projects/stats').then(r => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { loadStats(); }, [loadStats, statsV]);
  const refreshAll = () => { list.reload(); setStatsV(v => v + 1); };

  const kpis = stats ? [
    { k: 'total', v: stats.total, l: 'Proiecte', c: '#3b82f6', icon: FolderKanban },
    { k: 'active', v: stats.active, l: 'Active', c: '#10b981', icon: Activity },
    { k: 'value', v: money(stats.total_value), l: 'Valoare totală', c: '#a78bfa', icon: Wallet, wide: true },
    { k: 'ending', v: stats.ending_soon, l: 'Se încheie în 30 zile', c: '#f59e0b', icon: CalendarClock },
  ] : [];

  return <>
    <ErpHeading title="Proiecte & Contracte" count={stats ? `${stats.total} proiecte` : undefined}>
      {canWrite && <div className="flex gap-2"><Button variant="outline" onClick={() => setConverting(true)} data-testid="project-convert-btn"><GitBranch className="w-4 h-4 mr-2" />Din licitație</Button>
        <Button onClick={() => setForm({})} data-testid="project-add-btn"><Plus className="w-4 h-4 mr-2" />Proiect nou</Button></div>}
    </ErpHeading>

    <div className="asset-kpis" data-testid="project-kpis">{kpis.map(k => <div className="asset-kpi" key={k.k} style={{ '--kpi-accent': k.c, gridColumn: k.wide ? 'span 2' : undefined }} data-testid={`pkpi-${k.k}`}>
      <p className="kpi-val" style={{ color: k.c, fontSize: k.wide ? 22 : undefined }}>{k.v}</p><p className="kpi-lbl"><k.icon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{k.l}</p></div>)}</div>

    <div className="flex flex-wrap gap-3 items-center mb-6">
      <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Caută proiect…" className="pl-9" data-testid="project-search" /></div>
      <select value={status} onChange={e => setStatus(e.target.value)} className="bg-[#18181b] border border-[#3f3f46] rounded-md px-3 h-10 text-sm" data-testid="project-status-filter">
        <option value="">Toate stările</option>{Object.entries(PRJ_STATUS).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}</select>
    </div>

    <ErpStatus list={list} />
    {!list.loading && !list.error && <div className="space-y-2" data-testid="project-list">{list.items.map(p => {
      const st = PRJ_STATUS[p.status] || PRJ_STATUS.draft;
      return <button key={p.id} onClick={() => setDetailId(p.id)} className="w-full text-left erp-record !border rounded-xl px-4 hover:bg-white/[0.03] transition-colors" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`project-row-${p.id}`}>
        <div className="flex-1 min-w-0"><div className="flex items-center gap-2 flex-wrap"><p className="font-medium truncate">{p.name}</p><span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span></div>
          <p className="text-xs text-muted-foreground mt-1">{p.authority || '—'}{p.county ? ` · ${p.county}` : ''}{p.end_date ? ` · finalizare ${new Date(p.end_date).toLocaleDateString('ro-RO')}` : ''}</p></div>
        <span className="text-sm font-mono shrink-0">{money(p.value, p.currency)}</span></button>;
    })}</div>}
    {!list.loading && !list.error && list.items.length > 0 && <ErpPagination list={list} />}

    {form && <ProjectForm record={form.id ? form : null} onClose={() => setForm(null)} onSaved={refreshAll} />}
    {detailId && <ProjectDetail id={detailId} canWrite={canWrite} onClose={() => setDetailId('')} onEdit={r => { setDetailId(''); setForm(r); }} onChanged={refreshAll} />}
    {converting && <ConvertDialog onClose={() => setConverting(false)} onConverted={refreshAll} />}
  </>;
}
